'use client';

import { useState } from 'react';

import styles from './BackgroundVideo.module.css';
import {
  useBackgroundVideoGate,
  useDeferredPlayerMount,
  type BackgroundVideoFileVariant,
  type BackgroundVideoPoster as BackgroundVideoPosterConfig,
} from './backgroundVideo.shared';
import { BackgroundVideoPoster } from './BackgroundVideoPoster';

type BackgroundVideoFileProps = {
  variants: ReadonlyArray<BackgroundVideoFileVariant>;
  poster: BackgroundVideoPosterConfig;
  className?: string;
};

/**
 * Self-hosted background video: poster is LCP-safe; `<video>` mounts in-view +
 * idle. Skipped under reduced-motion or save-data preferences.
 * Pass narrow-then-wide `variants` so `<source media>` can pick a smaller encode.
 */
export function BackgroundVideoFile({ variants, poster, className }: BackgroundVideoFileProps) {
  const { ref, shouldPlay } = useBackgroundVideoGate();
  const playerReady = useDeferredPlayerMount(shouldPlay);
  const [videoPlaying, setVideoPlaying] = useState(false);

  return (
    <div ref={ref} className={[styles.root, className].filter(Boolean).join(' ')}>
      <BackgroundVideoPoster {...poster} hidden={videoPlaying && shouldPlay} />
      {playerReady ? (
        <video
          className={styles.video}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
          tabIndex={-1}
          onPlaying={() => setVideoPlaying(true)}
        >
          {variants.map(({ mp4, webm, media }) => (
            <VariantSources key={`${media ?? 'default'}:${mp4}`} mp4={mp4} webm={webm} media={media} />
          ))}
        </video>
      ) : null}
    </div>
  );
}

function VariantSources({ mp4, webm, media }: BackgroundVideoFileVariant) {
  return (
    <>
      {webm ? <source src={webm} type="video/webm" media={media} /> : null}
      <source src={mp4} type="video/mp4" media={media} />
    </>
  );
}
